// What the contract was read as, and what it will write - shown before anything posts.
//
// Used by both entry points (the Upload Contract modal and the chat), so the two cannot
// drift into showing different things or applying different rules.
//
// The header is a form, not a summary: a confirmation that arrives incomplete, or names
// a plant or seller the site does not recognise, is fixed here rather than sent back.
import { Alert, AlertTitle, Box, ToggleButton, Typography } from '@mui/material';
import { GetCustomerDataResponse } from '../../../../Generated/Commodity-Management-Api';
import EfpContractFieldsForm from './EfpContractFieldsForm';
import { EfpContractFields } from './EfpContractParser';
import { EfpResolution } from './EfpContractResolver';
import EfpLegGrid from './EfpLegGrid';
import { EfpBasisSource, EfpLeg, EfpPlan } from './EfpPositionPlan';
import { EfpImpliedBasis } from './EfpUnits';
import { StoneXToggleButtonGroup } from '../../../StoneXMui';
import { EfpEditableField, EfpEntityKind } from './useEfpContract';

export interface EfpContractReviewProps {
    fileName?: string;
    /** Current values - parsed, with any corrections applied. */
    fields: EfpContractFields;
    /** What was parsed before corrections. Defaults to `fields` when nothing is editable. */
    parsedFields?: EfpContractFields;
    /** Set when the basis in `fields` came from net less board rather than the form or the user. */
    impliedBasis?: EfpImpliedBasis;
    resolution: EfpResolution;
    customerData: GetCustomerDataResponse;
    /** Absent until everything needed to plan has resolved. */
    plan?: EfpPlan;
    /** Volume and price on a leg can be corrected before posting. */
    onChangeLeg?: (index: number, changes: Partial<Pick<EfpLeg, 'volume' | 'price'>>) => void;
    /** Header fields the confirmation got wrong or left blank can be corrected. */
    onChangeField?: <K extends EfpEditableField>(key: K, value: EfpContractFields[K] | undefined) => void;
    /** Names that did not resolve can be pointed at the right site record. */
    onChooseEntity?: (kind: EfpEntityKind, id: number | null) => void;
    /** When the contract's basis and the book's differ, the user says which to book. */
    onChooseBasisSource?: (source: EfpBasisSource) => void;
    disabled?: boolean;
}

export default function EfpContractReview(props: EfpContractReviewProps) {
    const {
        fileName, fields, parsedFields, impliedBasis, resolution, customerData, plan,
        onChangeLeg, onChangeField, onChooseEntity, onChooseBasisSource, disabled,
    } = props;

    const blockers = [
        ...resolution.missingFields.map(label => `${label} is missing from the contract`),
        ...resolution.unresolved,
        ...(plan?.errors ?? []),
    ];

    // Without both callbacks the form has nothing to report to, so it is read-only.
    const editable = !!onChangeField && !!onChooseEntity && !disabled;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {fileName && (
                <Typography variant="subtitle2" color="text.secondary">{fileName}</Typography>
            )}

            {blockers.length > 0 && (
                <Alert severity="error" variant="outlined">
                    <AlertTitle>
                        {blockers.length === 1 ? 'One thing needs fixing' : `${blockers.length} things need fixing`}
                        {editable ? ' - correct it below and the positions will update' : ''}
                    </AlertTitle>
                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                        {blockers.map(blocker => <li key={blocker}>{blocker}</li>)}
                    </ul>
                </Alert>
            )}

            {(plan?.warnings.length ?? 0) > 0 && (
                <Alert severity="warning" variant="outlined">
                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                        {plan!.warnings.map(warning => <li key={warning}>{warning}</li>)}
                    </ul>
                </Alert>
            )}

            <EfpContractFieldsForm
                fields={fields}
                parsedFields={parsedFields ?? fields}
                impliedBasis={impliedBasis}
                resolution={resolution}
                customerData={customerData}
                onChangeField={onChangeField ?? (() => undefined)}
                onChooseEntity={onChooseEntity ?? (() => undefined)}
                disabled={!editable}
            />

            {plan?.basisMismatch && (
                <Alert severity={plan.basisMismatch.source ? 'info' : 'error'} variant="outlined">
                    <AlertTitle>
                        {plan.basisMismatch.source ? 'Basis' : 'Which basis should this be booked at?'}
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        The contract {impliedBasis ? 'implies' : 'says'} {plan.basisMismatch.contractBasis}. The existing basis position for
                        {' '}{fields.deliveryMonth?.month}/{fields.deliveryMonth?.year} at
                        {' '}{resolution.location?.locationName} is {plan.basisMismatch.existingBasis}.
                    </Typography>
                    <StoneXToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        disableTextTransform
                        disabled={disabled || !onChooseBasisSource}
                        value={plan.basisMismatch.source ?? null}
                        onChange={(_, next: EfpBasisSource | null) => next && onChooseBasisSource?.(next)}
                    >
                        <ToggleButton value="contract">
                            Use the contract&apos;s {plan.basisMismatch.contractBasis}
                        </ToggleButton>
                        <ToggleButton value="existing">
                            Use the position&apos;s {plan.basisMismatch.existingBasis}
                        </ToggleButton>
                    </StoneXToggleButtonGroup>
                </Alert>
            )}

            {plan && (
                <>
                    <Typography variant="subtitle2">
                        This will write {plan.legs.length} positions
                        {plan.basisReduction ? ' and reduce an existing basis position' : ''}
                    </Typography>

                    {/* The futures price is the one number on the grid that is not on the
                        contract, so say where it came from. */}
                    {plan.futuresWeightedAveragePrice > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            The futures legs are priced at {plan.futuresWeightedAveragePrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                            {fields.unit ? ` per ${fields.unit}` : ''}, the average of the
                            {' '}{plan.parentOpenFuturesVolume.toLocaleString()}{fields.unit ? ` ${fields.unit}` : ''} of
                            {' '}{fields.futuresMonth ? `${fields.futuresMonth.month}/${fields.futuresMonth.year} ` : ''}
                            {resolution.commodity?.commodityName ?? ''} futures held long at
                            {' '}{resolution.parentLocation?.locationName ?? 'the parent location'}.
                            Only the physical and basis legs take their prices from the contract.
                        </Typography>
                    )}

                    <EfpLegGrid plan={plan} onChangeLeg={onChangeLeg} />
                </>
            )}

            {!plan && blockers.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Fill in the fields above to see the positions this will write.
                </Typography>
            )}
        </Box>
    );
}
